/**
 * AEO Demo Service
 *
 * API client for AEO Impact Visualization demo feature.
 */

import { authFetch } from '@/utils/auth-fetch';

// Types
export interface AEOElement {
  id: string;
  label: string;
  group: 'overview' | 'problem_solution' | 'buyer_guidance' | 'product_details' | 'qa';
  available: boolean;
  preview: string;
  wordCount: number;
}

export interface Position2D {
  x: number;
  y: number;
}

export interface FAQQuestion {
  id: number;
  text: string;
  category: string;
  position2d: Position2D;
  originalDistance: number;
  currentDistance?: number;
}

export interface MetricsSet {
  aeo: {
    intent_coverage: number;
    faq_quality: number;
    entity_richness: number;
    readability: number;
    conversational_usability: number;
  };
  seo: {
    schema_coverage: number;
    meta_optimization: number;
    internal_linking: number;
    content_depth: number;
    keyword_coverage: number;
  };
  ux: {
    ctas: number;
    trust_elements: number;
    media_quality: number;
    comparison_clarity: number;
  };
  scientific: {
    semanticDensity: number;
    answerCoverage: number;
    citationReadiness: number;
    specCompleteness: number;
  };
  overall: {
    aeo: number;
    seo: number;
    ux: number;
    total: number;
  };
}

export interface DemoSession {
  sessionId: string;
  product: {
    id: string;
    name: string;
    category: string;
    siteId: string;
    originalPageUrl: string;
  };
  aeoElements: AEOElement[];
  baselineMetrics: MetricsSet;
  competitorMetrics: CompetitorMetrics;
  faqQuestions: FAQQuestion[];
  presets: string[];
}

export interface FAQDistance {
  id: number;
  text: string;
  originalDistance: number;
  newDistance: number;
  percentChange: number;
}

export interface CalculateImpactResult {
  enhancedMetrics: MetricsSet;
  faqDistances: FAQDistance[];
  fromCache: boolean;
  calculationTime: number;
  preset?: string;
}

export interface PresetData {
  elements: string[];
  metrics: MetricsSet;
  distances: FAQDistance[];
}

export interface CuratedProduct {
  id: string;
  name: string;
  category: string;
  siteName: string;
}

export interface CompetitorMetrics {
  source: 'serp_analysis' | 'category_benchmark' | 'synthetic';
  name: string;
  url: string | null;
  domain: string;
  serpPosition: number;
  metrics: MetricsSet;
}

export interface SectionConfig {
  sections: Array<{ id: string; label: string; group: string }>;
  groups: Array<{ id: string; label: string }>;
  presets: Record<string, { elements: string[]; description: string }>;
  totalSections: number;
}

/**
 * Initialize a demo session for a product
 */
export async function initDemoSession(
  productId: string,
  mode: 'product' | 'standalone_demo' = 'product'
): Promise<DemoSession> {
  const response = await authFetch('/api/aeo-demo/init', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ productId, mode }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to initialize demo session');
  }

  const data = await response.json();
  return data;
}

/**
 * Calculate impact of selected AEO elements
 */
export async function calculateImpact(
  sessionId: string,
  selectedElements: string[],
  quickMode: boolean = false
): Promise<CalculateImpactResult> {
  const response = await authFetch(`/api/aeo-demo/${sessionId}/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selectedElements, quickMode }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to calculate impact');
  }

  const data = await response.json();
  return data;
}

/**
 * Get pre-computed presets for a session
 */
export async function getPresets(
  sessionId: string
): Promise<Record<string, PresetData>> {
  const response = await authFetch(`/api/aeo-demo/${sessionId}/presets`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get presets');
  }

  const data = await response.json();
  return data.presets;
}

/**
 * Get curated demo products
 */
export async function getCuratedProducts(): Promise<CuratedProduct[]> {
  const response = await authFetch('/api/aeo-demo/curated-products');

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get curated products');
  }

  const data = await response.json();
  return data.products;
}

/**
 * Get section configuration and presets metadata
 */
export async function getSectionConfig(): Promise<SectionConfig> {
  const response = await authFetch('/api/aeo-demo/section-config');

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get section config');
  }

  const data = await response.json();
  return data;
}
